import axios from 'axios'
import { delay } from '../../_augmentt/utils/utils'
import { TASK_ACTIONS, TASK_TYPES } from '../util/hub-task-management/hub.constants'
import { createHubTask, HUB_TASK_STATUS, pollTaskStatus } from './integrationHub.crud'

const ROOT = process.env.REACT_APP_AUGMENTT_API_URL

// Offboarding warnings are expected to arrive as an object keyed by warning context.
// Values can be frontend localization keys, legacy message objects, or JSON strings
// persisted by the hub task status document. Invalid warning strings are ignored.
const parseTaskWarnings = (warnings) => {
  if (!warnings) {
    return null
  }

  if (typeof warnings === 'string') {
    try {
      return JSON.parse(warnings)
    } catch (error) {
      return null
    }
  }

  return warnings
}

// <ROOT>/customer/:customerId/engage/scheduled-actions
export const scheduleUrl = ({ customerId }) => `${ROOT}/customer/${customerId}/engage/scheduled-actions`

// <ROOT>/customer/:customerId/engage/scheduled-actions/:scheduledActionId
export const scheduleItemUrl = ({ customerId, scheduledActionId }) =>
  `${scheduleUrl({ customerId })}/${scheduledActionId}`

// HTTP POST /customer/:customerId/engage/scheduled-actions
// {
//   target: <int>, // employeeId
//   start_time: <string>, // 2025-04-30T11:30:11.103Z
//   action: <string>, //
//   meta_data: <object>, // payload for hub task
//   created_by: <int>, // userId
// }
export const postEngageTaskSchedule = ({ customerId, payload }) => axios.post(scheduleUrl({ customerId }), payload)

// HTTP PUT /customer/:customerId/engage/scheduled-actions/:scheduledActionId
// {
//   start_time: <string>, // 2025-04-30T11:30:11.103Z
// }
export const putEngageTaskSchedule = async ({ customerId, scheduledActionId, payload }) =>
  axios.put(scheduleItemUrl({ customerId, scheduledActionId }), payload)

// HTTP DELETE /customer/:customerId/engage/scheduled-actions/:scheduledActionId
export const deleteEngageTaskSchedule = async ({ customerId, scheduledActionId }) =>
  axios.delete(scheduleItemUrl({ customerId, scheduledActionId }))

// HTTP GET /customer/:customerId/engage/scheduled-actions
export const getEngageTaskSchedule = async ({ customerId, status }) =>
  axios.get(scheduleUrl({ customerId }), { params: { status } })

// Offboarding hub task polling
export const offboardUser = async ({ customerId, organizationId, userId, parameters }) => {
  const hubTask = {
    actions: parameters,
    tasktype: TASK_TYPES.offboarding,
    action: TASK_ACTIONS.update,
    customerId,
    organizationId,
    userId,
  }

  const taskResponse = await createHubTask(hubTask)
  const { taskId } = taskResponse.data

  // wait 4 seconds before checking the backend so the task can be created
  await delay(4 * 1000)

  if (!taskId) {
    throw new Error('Failed to create task')
  }

  const pollTask = await pollTaskStatus(taskId, {
    retries: 180,
    timeout: 5000,
  })
  const taskResult = pollTask?.data?._source // eslint-disable-line no-underscore-dangle

  if (taskResult.status === HUB_TASK_STATUS.FAIL) {
    let message = 'Failed to perform action'
    try {
      const errors = JSON.parse(taskResult.errors)
      message = errors?.[0]?.innerError?.message || errors?.[0]?.message
    } catch (e) {
      // ignore
    }
    throw new Error(message)
  }

  return {
    data: {
      warnings: parseTaskWarnings(taskResult?.warnings),
    },
  }
}

export const resetPassword = async ({ customerId, organizationId, userId, actions }) => {
  const hubTask = {
    actions,
    tasktype: TASK_TYPES.resetPassword,
    action: TASK_ACTIONS.update,
    customerId,
    organizationId,
    userId,
  }

  const taskResponse = await createHubTask(hubTask)
  const { taskId } = taskResponse.data

  // wait 4 seconds before checking the backend so the task can be created
  await delay(4 * 1000)

  if (!taskId) {
    throw new Error('Failed to create task')
  }

  const pollTask = await pollTaskStatus(taskId, {
    retries: 180,
    timeout: 5000,
  })
  const taskResult = pollTask?.data?._source // eslint-disable-line no-underscore-dangle

  if (taskResult.status === HUB_TASK_STATUS.FAIL) {
    let message = 'Failed to perform action'
    try {
      const errors = JSON.parse(taskResult.errors)
      const firstErrorMessage = typeof errors?.[0] === 'string' ? errors?.[0] : ''

      message = errors?.[0]?.innerError?.message || errors?.[0]?.message || firstErrorMessage || message
    } catch (e) {
      // ignore
    }
    throw new Error(message)
  }
}

export const updateUserProperties = async ({
  customerId,
  organizationId,
  userId,
  employeeIdentifier,
  userProperties,
}) => {
  const hubTask = {
    actions: {
      employeeIdentifier,
      userProperties,
    },
    tasktype: TASK_TYPES.updateUser,
    action: TASK_ACTIONS.update,
    customerId,
    organizationId,
    userId,
    requiresManagementTaskRefresh: true,
  }

  const taskResponse = await createHubTask(hubTask)
  const { taskId } = taskResponse.data

  // wait 4 seconds before checking the backend so the task can be created
  await delay(4 * 1000)

  if (!taskId) {
    throw new Error('Failed to create task')
  }

  const pollTask = await pollTaskStatus(taskId, {
    retries: 25,
    timeout: 3000,
  })
  const taskResult = pollTask?.data?._source // eslint-disable-line no-underscore-dangle

  if (taskResult.status === HUB_TASK_STATUS.FAIL) {
    let message = 'Failed to update user properties'
    try {
      const errors = JSON.parse(taskResult.errors)
      const firstErrorMessage = typeof errors?.[0] === 'string' ? errors?.[0] : ''

      message = errors?.[0]?.innerError?.message || errors?.[0]?.message || firstErrorMessage || message
    } catch (e) {
      // ignore
    }
    throw new Error(message)
  }
}
